// Единственный locale owner FreeHub shell (Story 1.7 / FR-4).
//
// Границы ответственности:
//   * решения «какой locale активен» и «где он хранится»;
//   * чистые функции перевода/форматирования над каталогами en/ru;
//   * безопасный fallback: отсутствующий перевод → English, неизвестный ключ →
//     безопасный generic текст (raw key не показывается пользователю никогда).
//
// Модуль не владеет DOM, сетью или состоянием приложения: `app.js` только
// подключает его как минимальный wiring. Нативный ESM, без зависимостей.

import en from "./locales/en.js";
import ru from "./locales/ru.js";

export const SUPPORTED_LOCALES = Object.freeze(["en", "ru"]);
export const DEFAULT_LOCALE = "en";
export const LOCALE_STORAGE_KEY = "freehub.locale.v1";

/** Каталоги по locale. Значение — строка или объект plural-форм CLDR. */
export const CATALOGS = Object.freeze({ en, ru });

/** BCP-47 теги для Intl: только en-US/ru-RU, без авто-выбора языка браузера. */
export const INTL_LOCALES = Object.freeze({ en: "en-US", ru: "ru-RU" });

/** Обязательные plural-формы по locale (CLDR cardinal). */
export const PLURAL_FORMS = Object.freeze({
  en: Object.freeze(["one", "other"]),
  ru: Object.freeze(["one", "few", "many", "other"]),
});

// Безопасный текст вместо неизвестного ключа: не содержит raw key и кода.
const GENERIC_TEXT = Object.freeze({
  en: "Something went wrong. Please try again.",
  ru: "Что-то пошло не так. Повторите попытку.",
});

let activeLocale = DEFAULT_LOCALE;

/** `en`/`ru`, иначе null — «явно поддерживаемый locale». */
export function normalizeLocale(value) {
  return typeof value === "string" && SUPPORTED_LOCALES.includes(value) ? value : null;
}

/** `en`/`ru` с fallback на default `en` для любых неподдерживаемых входов. */
export function resolveLocale(value) {
  return normalizeLocale(value) ?? DEFAULT_LOCALE;
}

export function intlLocale(value) {
  return INTL_LOCALES[resolveLocale(value)];
}

/** Активный locale текущего runtime (для модулей, не владеющих app-состоянием). */
export function getActiveLocale() {
  return activeLocale;
}

/** Смена активного locale; невалидное значение игнорируется. */
export function setActiveLocale(value) {
  const normalized = normalizeLocale(value);
  if (normalized) activeLocale = normalized;
  return activeLocale;
}

function isUsableEntry(entry) {
  if (typeof entry === "string") return entry !== "";
  return Boolean(entry) && typeof entry === "object";
}

/**
 * Каталог для locale с English fallback и языком каталога, который реально
 * используется. Язык-владелец важен для plural: строки/формы берутся из каталога
 * конкретного языка, поэтому и правило выбора формы должно быть его же (иначе
 * English fallback получал бы RU-правила `few`/`many` и печатал «2 document»).
 */
function catalogFor(catalogs, value) {
  const locale = resolveLocale(value);
  const own = catalogs ? catalogs[locale] : undefined;
  if (own) return { catalog: own, locale };
  const fallback = catalogs ? catalogs[DEFAULT_LOCALE] : undefined;
  return { catalog: fallback, locale: DEFAULT_LOCALE };
}

/**
 * Значение ключа вместе с языком каталога, откуда значение реально взято:
 * из активного locale, иначе English fallback, иначе undefined.
 */
function entryFor(catalogs, value, key) {
  const primary = catalogFor(catalogs, value);
  const own = primary.catalog ? primary.catalog[key] : undefined;
  if (isUsableEntry(own)) return { entry: own, locale: primary.locale };
  const fallbackCatalog = catalogs ? catalogs[DEFAULT_LOCALE] : undefined;
  const fallback = fallbackCatalog ? fallbackCatalog[key] : undefined;
  if (isUsableEntry(fallback)) return { entry: fallback, locale: DEFAULT_LOCALE };
  return { entry: undefined, locale: primary.locale };
}

function interpolate(template, variables) {
  return template.replace(/\{(\w+)\}/g, (match, name) => {
    const value = variables[name];
    return value === undefined || value === null ? match : String(value);
  });
}

/** Plural-форма CLDR для числа в locale (`Intl.PluralRules`). */
export function pluralForm(value, count) {
  const number = Number(count);
  if (!Number.isFinite(number)) return "other";
  return new Intl.PluralRules(intlLocale(value)).select(number);
}

/**
 * Перевод ключа. Пустой ключ → пустая строка (пустые notices не рендерятся).
 * Неизвестный ключ → безопасный generic текст текущего locale, никогда не ключ.
 * `catalogs` — минимальный test seam: те же чистые функции над инъецируемым
 * каталогом (в production всегда реальные en/ru).
 */
export function translate(value, key, variables = {}, catalogs = CATALOGS) {
  if (typeof key !== "string" || key === "") return "";
  const locale = resolveLocale(value);
  const found = entryFor(catalogs, locale, key);
  const entry = found.entry;

  if (typeof entry === "string") return interpolate(entry, variables);

  if (entry && typeof entry === "object") {
    const count = Number(variables.count ?? 0);
    // Форма выбирается по языку каталога, откуда реально взят перевод: English
    // fallback не должен получать RU-правила (и наоборот), иначе «2 documents»
    // превращается в «2 document».
    const forms = PLURAL_FORMS[found.locale] ?? PLURAL_FORMS[DEFAULT_LOCALE];
    const preferred = pluralForm(found.locale, count);
    const ordered = [preferred, ...forms.filter((form) => form !== preferred)];
    for (const form of ordered) {
      if (typeof entry[form] === "string" && entry[form] !== "") {
        return interpolate(entry[form], { ...variables, count });
      }
    }
  }

  return GENERIC_TEXT[locale];
}

/** Перевод plural-ключа для числа (ключ с объектом форм CLDR). */
export function plural(value, key, count, variables = {}, catalogs = CATALOGS) {
  return translate(value, key, { ...variables, count }, catalogs);
}

/** Локализованная дата. Невалидная/пустая дата → пустая строка. */
export function formatDate(value, date, options) {
  if (date === null || date === undefined || date === "") return "";
  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";
  const formatter = new Intl.DateTimeFormat(
    intlLocale(value),
    options ?? { dateStyle: "short", timeStyle: "short" },
  );
  return formatter.format(parsed);
}

/** Локализованное число. Нечисловое/бесконечное значение → пустая строка. */
export function formatNumber(value, number, options) {
  const parsed = Number(number);
  if (!Number.isFinite(parsed)) return "";
  return new Intl.NumberFormat(intlLocale(value), options).format(parsed);
}

/**
 * Чтение preference. Хранится только `en`/`ru`; любое другое значение —
 * default `en`. Недоступное/запрещённое storage не ломает UI.
 */
export function readStoredLocale(storage) {
  if (!storage || typeof storage.getItem !== "function") return DEFAULT_LOCALE;
  try {
    return normalizeLocale(storage.getItem(LOCALE_STORAGE_KEY)) ?? DEFAULT_LOCALE;
  } catch (_error) {
    return DEFAULT_LOCALE;
  }
}

/** Запись preference. Возвращает false, если storage недоступен (memory fallback). */
export function writeStoredLocale(storage, value) {
  const normalized = normalizeLocale(value);
  if (!normalized) return false;
  if (!storage || typeof storage.setItem !== "function") return false;
  try {
    storage.setItem(LOCALE_STORAGE_KEY, normalized);
    return true;
  } catch (_error) {
    return false;
  }
}

/**
 * Контроллер preference: держит текущий locale, читает/пишет его через переданную
 * storage-границу и уведомляет подписчиков. При недоступном storage значение
 * остаётся в памяти текущего runtime (persistence через reload не обещается).
 */
export function createLocaleController({ storage = null, initial = null } = {}) {
  let current = normalizeLocale(initial) ?? readStoredLocale(storage);
  const listeners = new Set();

  return {
    get locale() {
      return current;
    },
    setLocale(next) {
      const normalized = normalizeLocale(next);
      if (!normalized || normalized === current) return current;
      current = normalized;
      writeStoredLocale(storage, normalized);
      for (const listener of listeners) listener(current);
      return current;
    },
    subscribe(listener) {
      if (typeof listener !== "function") return () => {};
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
